import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { analyzeRange, type Interval } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle } from '../feature-style.model';
import type { ComputedCollection } from '../../../context/computed-collection';
import type { RangeParams } from './range.model';

/**
 * Creates range visualization for functions.
 *
 * Shows which y-values a function outputs using colored horizontal band
 * polygons. Each band spans the full viewport width and covers a y-interval
 * where the function produces values.
 *
 * The visualization is reactive and updates when:
 * - The viewport (bounding box) changes
 * - The parent function parameters change
 */
export class RangeCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'range';
  override readonly computedValueKeys = ['intervalCount'] as const;
  override readonly computedCollectionKeys = [{ name: '', properties: ['start', 'end'] }] as const;

  /** Maximum number of intervals to display */
  private readonly maxIntervals = 20;

  /** Width of polygons (very large to cover any viewport) */
  private readonly polygonWidth = 10000;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory } = context;
    const params = def.params as RangeParams | undefined;

    if (!params?.of) {
      throw new Error('Range feature requires params.of to specify the parent function');
    }

    const parentFn = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    // Resolve polygon style
    const polygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, []);
    const showPolygons = polygonStyle.visible !== false;

    // =========================================================================
    // PERFORMANCE OPTIMIZATION: Cache interval calculations per render frame
    // =========================================================================
    let cachedIntervals: Interval[] = [];
    let cacheKey = '';

    const getIntervals = (): Interval[] => {
      const bbox = board.getBoundingBox();
      const fnParams = parentFn.getParams();
      const paramsKey = Object.values(fnParams)
        .map((v) => v.toFixed(4))
        .join(',');
      const newKey = `${bbox[0].toFixed(2)},${bbox[2].toFixed(2)}|${paramsKey}`;

      if (newKey !== cacheKey) {
        cacheKey = newKey;
        cachedIntervals = analyzeRange((x) => parentFn.evaluate(x), bbox[0], bbox[2], {
          samples: 500,
        });
      }
      return cachedIntervals;
    };

    const getInterval = (index: number): Interval | null => {
      const intervals = getIntervals();
      return index < intervals.length ? intervals[index] : null;
    };

    // =========================================================================
    // Create placeholder polygons (horizontal bands)
    // =========================================================================

    if (showPolygons) {
      for (let i = 0; i < this.maxIntervals; i++) {
        const index = i;

        const polygonResult = elementFactory.polygon({
          vertices: [
            [-this.polygonWidth, () => getInterval(index)?.start ?? NaN],
            [this.polygonWidth, () => getInterval(index)?.start ?? NaN],
            [this.polygonWidth, () => getInterval(index)?.end ?? NaN],
            [-this.polygonWidth, () => getInterval(index)?.end ?? NaN],
          ],
          style: {
            ...polygonStyle,
            strokeWidth: 0,
            vertex: { visible: false },
            border: { visible: false },
          },
        });

        polygonResult.polygon.setAttribute({
          visible: () => !!getInterval(index),
          layer: 0,
        });

        objects.push(polygonResult.polygon, ...polygonResult.vertices, ...polygonResult.borders);
      }
    }

    // =========================================================================
    // Create boundary lines at finite interval endpoints
    // =========================================================================

    const lineStyle = resolveFeatureStyle('line', this.featureType, context, display, []);
    const showLines = lineStyle.visible !== false;

    if (showLines) {
      const labelPrecision = (typeof lineStyle.label === 'object' ? lineStyle.label?.precision : undefined) ?? 2;

      /** Check if a y-value is at the viewport edge (extends to infinity) */
      const isAtViewportEdge = (y: number, bbox: number[]): boolean => {
        // bbox: [xMin, yMax, xMax, yMin]
        const tolerance = (bbox[1] - bbox[3]) * 0.01;
        return y <= bbox[3] + tolerance || y >= bbox[1] - tolerance;
      };

      // Pre-allocate maxIntervals * 2 line slots (start + end per interval)
      for (let i = 0; i < this.maxIntervals * 2; i++) {
        const intervalIndex = Math.floor(i / 2);
        const isStart = i % 2 === 0;

        const getY = (): number => {
          const interval = getInterval(intervalIndex);
          if (!interval) {
            return NaN;
          }
          const bbox = board.getBoundingBox();
          const y = isStart ? interval.start : interval.end;
          return isAtViewportEdge(y, bbox) ? NaN : y;
        };

        const lineResult = elementFactory.line({
          a: [-this.polygonWidth, getY],
          b: [this.polygonWidth, getY],
          name: () => {
            const y = getY();
            return isNaN(y) ? '' : y.toFixed(labelPrecision);
          },
          style: {
            ...lineStyle,
            straightFirst: true,
            straightLast: true,
            vertex: { visible: false },
          },
        });

        lineResult.line.setAttribute({ visible: () => !isNaN(getY()) });
        if (lineResult.lineLabel) {
          lineResult.lineLabel.setAttribute({ visible: () => !isNaN(getY()) });
          objects.push(lineResult.lineLabel);
        }
        objects.push(lineResult.line, lineResult.a, lineResult.b);
      }
    }

    // Computed values
    const computedValues: Record<string, () => number> = {
      intervalCount: () => getIntervals().length,
    };

    const collection: ComputedCollection = {
      getCount: () => getIntervals().length,
      maxItems: this.maxIntervals,
      getItem: (index: number) => {
        const intervals = getIntervals();
        if (index < 0 || index >= intervals.length) {
          return undefined;
        }
        const interval = intervals[index];
        return {
          start: () => interval.start,
          end: () => interval.end,
        };
      },
      properties: ['start', 'end'],
    };

    return {
      id,
      type: 'range',
      objects,
      getComputedValues: () => computedValues,
      getComputedCollections: () => ({ '': collection }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
